import { LitElement, css, html } from "lit";
import { fa } from "../../utils/widgets.js";
import { faStar } from "@fortawesome/free-solid-svg-icons";

/**
 * Grade Filter allows to filter picture by their grade (for example, precision grade)
 * @class Panoramax.components.ui.GradeFilter
 * @element pnx-grade-filter
 * @extends [lit.LitElement](https://lit.dev/docs/api/LitElement/)
 * @fires Panoramax.components.ui.GradeFilter#change
 * @example
 * ```html
 * <pnx-grade-filter grade="5" input="pnx-g-input" @change=${() => alert('changed')} ._t=${viewer._t} />
 * ```
 */
export default class GradeFilter extends LitElement {
	/** @private */
	static styles = css`
		:host {
			gap: 0;
			align-items: stretch;
			display: flex;
		}
		label {
			display: inline-flex;
			padding: 2px 7px;
			background: none;
			border: 1px solid var(--widget-border-btn);
			color: var(--widget-font-direct);
			cursor: pointer;
			font-size: 16px;
			text-decoration: none;
			border-left-width: 0px;
		}
		label:hover {
			background-color: var(--widget-bg-hover);
		}
		label:first-of-type {
			border-top-left-radius: 8px;
			border-bottom-left-radius: 8px;
			border-left-width: 1px;
		}
		label:last-of-type {
			border-top-right-radius: 8px;
			border-bottom-right-radius: 8px;
		}
		label svg {
			display: block;
			width: 0.8em;
		}
		input[type="checkbox"] { display: none; }
		input[type="checkbox"]:checked + label {
			background-color: var(--widget-bg-active);
			color: var(--widget-font-active);
		}
		input[type="checkbox"]:checked + label:first-of-type {
			border-right-color: white;
		}
	`;

	/**
	 * Component properties.
	 * @memberof Panoramax.components.ui.GradeFilter#
	 * @type {Object}
	 * @property {string} input ID prefix for input fields
	 * @property {number|string} [grade=0] The grade (5 to 1 = precise grade, 0 = none selected), or a list of grades
	 * @property {Object} labels Helper labels for each grade, as {"1": "L1", "2": "L2", ...}
	 */
	static properties = {
		grade: {type: String},
		input: {type: String},
		labels: {type: Object},
	};

	constructor() {
		super();
		this.grade = 0;
		this.input = "";
		this.labels = {};
	}

	/** @private */
	_onInput() {
		// List all checked boxes
		const newgrade = [];
		for(let i of this.renderRoot.querySelectorAll("input")) {
			if(i.checked) { newgrade.push(i.value); }
		}
		this.grade = newgrade.join(",");

		/**
		 * Event for grade change
		 * @event Panoramax.components.ui.GradeFilter#change
		 * @type {CustomEvent}
		 */
		this.dispatchEvent(new CustomEvent("change", {bubbles: true, composed: true}));
	}

	/** @private */
	render() {
		const grades = `${this.grade}`.split(",").map(v => parseInt(v)).filter(v => !isNaN(v));

		return [5,4,3,2,1].map(v => html`
			<input
				id="${this.input}-${v}"
				type="checkbox"
				name=${this.input}
				value=${v}
				@change="${this._onInput}"
				.checked=${grades.includes(v)}
			/>
			<label
				for="${this.input}-${v}"
				title=${this.labels?.[v] ? this.labels[v] : null}
			>
				${v} ${fa(faStar)}
			</label>
		`);
	}
}

customElements.define("pnx-grade-filter", GradeFilter);
